import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Select, Button, Drawer, Modal as AntdModal, Form, Input, AutoComplete, DatePicker, Switch, Spin, Tooltip, message } from "antd";
import dayjs from "dayjs";
import { fetchOrgChart, saveOrgPerson, moveOrgPerson, setOrgPersonActive } from "./orgChartApi";
import "./Orgchart.css";

// Left bar + light background by level (top → down)
const LEVELS = [
  { bar: "#6E8EF5", bg: "#F1F5FF" },
  { bar: "#2EC4C9", bg: "#EFFBFB" },
  { bar: "#F28DB2", bg: "#FFF5F9" },
  { bar: "#B98AE0", bg: "#FAF5FF" },
  { bar: "#4CC9A0", bg: "#F0FBF6" },
];
const AVATAR_COLORS = ["#1D5FD1", "#0E9F9A", "#C2410C", "#7C3AED", "#BE185D", "#15803D", "#B45309", "#0369A1"];
const DRAG_START_PX = 6;     // the pointer must move this far before a press becomes a drag (a click stays a click)
const EDGE_PX = 70;          // auto-scroll when the pointer is this close to the chart's edge
const OPEN_TEAM_MS = 600;    // hold over a closed team this long to open it
const ZOOM_MIN = 0.2, ZOOM_MAX = 2;
const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

const initials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : parts[0][1] || "")).toUpperCase();
};
const colorOf = (name) => {
  let h = 0;
  for (const c of String(name || "")) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const fmtDate = (iso) => (iso ? dayjs(iso).format("DD MMM YYYY") : "-");
const STATUSES = ["Active", "Notice Period", "Relieved"];

const Avatar = ({ name, inactive }) => (
  <span className="oc-avatar" style={{ background: inactive ? "#9AA3B2" : colorOf(name) }} aria-hidden="true">
    {initials(name)}
  </span>
);

const Field = ({ label, children }) => (
  <div className="oc-field">
    <label>{label}</label>
    <div>{children || "-"}</div>
  </div>
);

export const OrgChart = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [state, setState] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  const [savedPeople, setSavedPeople] = useState([]);   // as saved in the database
  const [regions, setRegions] = useState([]);
  const [canEdit, setCanEdit] = useState(false);

  const [collapsed, setCollapsed] = useState(() => new Set());
  const collapseInit = useRef(false);
  const [selectedId, setSelectedId] = useState(null);
  const [foundId, setFoundId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  // View like greytHR: mouse wheel zooms at the pointer, dragging empty space moves the whole chart
  const [view, setView] = useState({ z: 1, x: 0, y: 0 });   // zoom + position of the chart inside the canvas
  const zoom = view.z;
  const [smooth, setSmooth] = useState(false);              // animate buttons / Fit / search, not wheel or drag
  const [panning, setPanning] = useState(false);
  const centered = useRef(false);
  const [editing, setEditing] = useState(null);        // null | person being edited | { id: 0, managerId } for new
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // Moving people: every move is saved at once
  const [pinnedTop, setPinnedTop] = useState(() => new Set()); // put on "Top level" this visit (shown even without a team)
  const [movingIds, setMovingIds] = useState(() => new Set()); // moves still being saved
  const [showPalette, setShowPalette] = useState(false);       // "All employees" list to drag from
  const [paletteFilter, setPaletteFilter] = useState("");
  const [drag, setDrag] = useState(null);                      // { id, x, y, target, ok } while dragging
  const [moveFor, setMoveFor] = useState(null);                // "Move…" mode: id of the person being moved

  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

  // ── Load ──
  const load = useCallback(async () => {
    try {
      const d = await fetchOrgChart();
      setSavedPeople(d.people);
      setRegions(d.regions);
      setCanEdit(d.canEdit);
      setState("ready");
    } catch (err) {
      console.error("Org chart load failed:", err);
      setError(err.message || "Could not load the org chart.");
      setState("error");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ── Structure ──
  const people = savedPeople;
  const active = useMemo(() => people.filter((p) => p.active), [people]);
  const inactive = useMemo(() => people.filter((p) => !p.active), [people]);
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const activeIds = useMemo(() => new Set(active.map((p) => p.id)), [active]);
  // Manager only counts if they're active (inactive people never have a team on the chart)
  const managerOf = useCallback((p) => (p.managerId && activeIds.has(p.managerId) ? p.managerId : null), [activeIds]);

  const children = useMemo(() => {
    const m = new Map();
    for (const p of active) {
      const mgr = managerOf(p);
      if (mgr == null) continue;
      if (!m.has(mgr)) m.set(mgr, []);
      m.get(mgr).push(p);
    }
    for (const list of m.values()) list.sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
    return m;
  }, [active, managerOf]);

  const kids = useCallback((id) => children.get(id) || [], [children]);
  const roots = useMemo(
    () => active.filter((p) => managerOf(p) == null && (kids(p.id).length > 0 || pinnedTop.has(p.id)))
                .sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name)),
    [active, managerOf, kids, pinnedTop]
  );
  const unplaced = useMemo(
    () => active.filter((p) => managerOf(p) == null && kids(p.id).length === 0 && !pinnedTop.has(p.id)).sort((a, b) => a.name.localeCompare(b.name)),
    [active, managerOf, kids, pinnedTop]
  );

  // Everyone in a person's team (all levels)
  const teamOf = useCallback((id) => {
    const out = new Set();
    const walk = (x) => kids(x).forEach((c) => { if (!out.has(c.id)) { out.add(c.id); walk(c.id); } });
    walk(id);
    return out;
  }, [kids]);

  const depthOf = useCallback((id) => {
    let d = 0, cur = byId.get(id), guard = 0;
    while (cur && managerOf(cur) != null && guard++ < 50) { d++; cur = byId.get(managerOf(cur)); }
    return d;
  }, [byId, managerOf]);

  // First view: two levels (top people + their direct reports). Click a number to open a team.
  useEffect(() => {
    if (state !== "ready" || collapseInit.current) return;
    collapseInit.current = true;
    const next = new Set();
    for (const p of active) if (kids(p.id).length && depthOf(p.id) >= 1) next.add(p.id);
    setCollapsed(next);
  }, [state, active, kids, depthOf]);

  const toggle = (id) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(active.filter((p) => kids(p.id).length && managerOf(p) != null).map((p) => p.id)));

  // ── Zoom & move (the chart is moved with a transform, so it can go anywhere in the canvas) ──
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setStageSize({ w: el.offsetWidth, h: el.offsetHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [state, roots.length > 0]);

  const animate = () => { setSmooth(true); clearTimeout(animate.t); animate.t = setTimeout(() => setSmooth(false), 320); };
  // Zoom keeping the point (px, py) of the canvas where it is (the pointer, or the canvas centre)
  const zoomAt = (nextZ, px, py) =>
    setView((v) => {
      const z = clampZoom(typeof nextZ === "function" ? nextZ(v.z) : nextZ), k = z / v.z;
      return { z, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
    });
  const canvasCentre = () => {
    const c = canvasRef.current;
    return c ? [c.clientWidth / 2, c.clientHeight / 2] : [0, 0];
  };
  const zoomBy = (d) => { animate(); const [cx, cy] = canvasCentre(); zoomAt((z) => Math.round((z + d) * 10) / 10, cx, cy); };
  const zoomTo100 = () => { animate(); const [cx, cy] = canvasCentre(); zoomAt(1, cx, cy); };
  // Whole chart on screen, centred
  const fit = () => {
    const c = canvasRef.current;
    if (!c || !stageSize.w) return;
    const z = clampZoom(Math.min(1, (c.clientWidth - 32) / stageSize.w, (c.clientHeight - 32) / stageSize.h));
    animate();
    setView({ z, x: (c.clientWidth - stageSize.w * z) / 2, y: Math.max(16, (c.clientHeight - stageSize.h * z) / 2) });
  };
  // First load: centre the chart at the top (after the first view has closed the deeper teams)
  const [, setShown] = useState(false);
  useEffect(() => {
    if (state !== "ready" || centered.current) return undefined;
    const t = setTimeout(() => {
      const c = canvasRef.current, st = stageRef.current;
      centered.current = true;
      if (c && st) setView({ z: 1, x: (c.clientWidth - st.offsetWidth) / 2, y: 16 });
      setShown(true);   // show the chart even if there was nothing to measure
    }, 80);
    return () => clearTimeout(t);
  }, [state, roots.length > 0]);

  // Mouse wheel / trackpad = zoom at the pointer (native listener: React's wheel handler can't stop the page scrolling)
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = c.getBoundingClientRect();
      const dy = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
      const factor = Math.exp(-Math.max(-300, Math.min(300, dy)) * 0.0015);
      zoomAt((z) => z * factor, e.clientX - r.left, e.clientY - r.top);
    };
    c.addEventListener("wheel", onWheel, { passive: false });
    return () => c.removeEventListener("wheel", onWheel);
  }, [state, roots.length > 0]);

  // Drag empty space (not a card or button) to move the whole chart. Works with mouse, pen and touch.
  const panRef = useRef(null);
  const onCanvasPointerDown = (e) => {
    if (e.button !== 0 || session.current) return;
    if (e.target.closest && e.target.closest(".oc-card, button, a, input, .oc-topzone, .ant-select")) return;
    panRef.current = { sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y, id: e.pointerId };
    setPanning(true);
    const move = (ev) => {
      const p = panRef.current;
      if (!p || ev.pointerId !== p.id) return;
      setView((v) => ({ ...v, x: p.vx + ev.clientX - p.sx, y: p.vy + ev.clientY - p.sy }));
    };
    const up = () => {
      panRef.current = null;
      setPanning(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  // ── Search → open the path, select, scroll to the card ──
  const reveal = (id) => {
    setCollapsed((s) => {
      const n = new Set(s);
      let cur = byId.get(id), guard = 0;
      while (cur && managerOf(cur) != null && guard++ < 50) { n.delete(managerOf(cur)); cur = byId.get(managerOf(cur)); }
      return n;
    });
    setSelectedId(id);
    setFoundId(id);
  };
  useEffect(() => {
    if (!foundId) return;
    const t1 = setTimeout(() => {
      const card = document.getElementById(`oc-node-${foundId}`), c = canvasRef.current;
      if (!card) return;
      if (!c || !c.contains(card)) { card.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" }); return; }
      // Move the chart so the card is in the middle of the canvas
      const cr = c.getBoundingClientRect(), kr = card.getBoundingClientRect();
      animate();
      setView((v) => ({ ...v, x: v.x + (cr.left + cr.width / 2) - (kr.left + kr.width / 2),
                              y: v.y + (cr.top + cr.height / 2) - (kr.top + kr.height / 2) }));
    }, 60);
    const t2 = setTimeout(() => setFoundId(null), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [foundId]);

  const searchOptions = useMemo(
    () => [...active, ...(showInactive ? inactive : [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => ({
        value: p.id,
        label: `${p.name}${p.designation ? ` · ${p.designation}` : ""} · ${p.code}${p.active ? "" : " (relieved)"}`,
      })),
    [active, inactive, showInactive]
  );

  // ── Can src report to target? (target "top" = no manager) ──
  const canDropOn = (srcId, targetId) => {
    if (!canEdit || srcId == null || targetId == null || srcId === targetId) return false;
    const src = byId.get(srcId);
    if (!src || !src.active || movingIds.has(srcId)) return false;
    // top level: people who have a manager, or someone from "No manager yet" (to start a new top)
    if (targetId === "top") return managerOf(src) != null || (kids(srcId).length === 0 && !pinnedTop.has(srcId));
    const tgt = byId.get(targetId);
    if (!tgt || !tgt.active) return false;
    if (managerOf(src) === targetId) return false;
    return !teamOf(srcId).has(targetId);   // not into their own team (no loops)
  };
  const whyNot = (srcId, targetId) => {
    if (targetId == null) return "Move over a manager's card or team";
    if (targetId === srcId) return "That's the same person";
    const src = byId.get(srcId);
    if (targetId === "top") return "Already at the top level";
    if (src && managerOf(src) === targetId) return `Already reports to ${byId.get(targetId)?.name}`;
    if (teamOf(srcId).has(targetId)) return `${byId.get(targetId)?.name} is in ${src?.name}'s own team`;
    return "Can't move here";
  };

  // Show the move at once, save it, roll it back if the server refuses. targetId null = top level.
  const setManagerLocal = (id, managerId) =>
    setSavedPeople((list) => list.map((x) => (x.id === id ? { ...x, managerId } : x)));
  const moveNow = async (srcId, targetId, { isUndo = false, pinned } = {}) => {
    const src = byId.get(srcId);
    if (!src) return;
    const before = src.managerId ?? null;
    const pinnedBefore = pinnedTop.has(srcId);
    setManagerLocal(srcId, targetId);
    // Top level by hand = pinned; Undo puts back exactly what it was
    const pinNow = pinned !== undefined ? pinned : targetId == null;
    setPinnedTop((st) => { const n = new Set(st); if (pinNow) n.add(srcId); else n.delete(srcId); return n; });
    if (targetId != null) setCollapsed((c) => { const n = new Set(c); n.delete(targetId); return n; });
    setMovingIds((m) => new Set(m).add(srcId));
    setFoundId(srcId);
    try {
      const msg = await moveOrgPerson(srcId, targetId);
      const key = `oc-move-${srcId}-${Date.now()}`;
      messageApi.open({
        key,
        type: "success",
        duration: 6,
        content: isUndo ? msg : (
          <span>
            {msg}{" "}
            <a onClick={() => { messageApi.destroy(key); moveNow(srcId, before, { isUndo: true, pinned: pinnedBefore }); }} style={{ marginLeft: 8, fontWeight: 600 }}>
              Undo
            </a>
          </span>
        ),
      });
    } catch (err) {
      setManagerLocal(srcId, before);   // not saved → back where it was
      setPinnedTop((st) => { const n = new Set(st); if (pinnedBefore) n.add(srcId); else n.delete(srcId); return n; });
      messageApi.error(err.message);
    } finally {
      setMovingIds((m) => { const n = new Set(m); n.delete(srcId); return n; });
    }
  };

  // ── Drag with the pointer (Admin / HR) ──
  // Drop target = the nearest element marked data-oc-drop under the pointer: a card, a manager's whole team area
  // (<li>), or the "top level" bar. elementFromPoint works with the zoom transform, so no maths is needed.
  const session = useRef(null);         // { id, sx, sy, x, y, started, target }
  const suppressClick = useRef(false);  // a drag ends with a click event on the card: ignore that one
  const hoverOpen = useRef({ id: null, timer: null });
  const scrollTimer = useRef(null);

  const targetAt = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const hit = el && el.closest ? el.closest("[data-oc-drop]") : null;
    if (!hit) return null;
    const v = hit.getAttribute("data-oc-drop");
    return v === "top" ? "top" : Number(v);
  };
  const stopHoverOpen = () => { clearTimeout(hoverOpen.current.timer); hoverOpen.current = { id: null, timer: null }; };
  const hoverToOpen = (id) => {
    if (hoverOpen.current.id === id) return;
    stopHoverOpen();
    hoverOpen.current = {
      id,
      timer: typeof id === "number" && collapsed.has(id) && kids(id).length
        ? setTimeout(() => setCollapsed((c) => { const n = new Set(c); n.delete(id); return n; }), OPEN_TEAM_MS)
        : null,
    };
  };

  // Latest handlers in a ref, so the window listeners (added once per drag) always see current state
  const handlers = useRef({});
  handlers.current.move = (e) => {
    const s = session.current;
    if (!s) return;
    s.x = e.clientX; s.y = e.clientY;
    if (!s.started) {
      if (Math.hypot(e.clientX - s.sx, e.clientY - s.sy) < DRAG_START_PX) return;
      s.started = true;
      document.body.classList.add("oc-drag-active");
      // keep scrolling while the pointer rests near an edge
      scrollTimer.current = setInterval(() => {
        const c = canvasRef.current, cur = session.current;
        if (!c || !cur || !cur.started) return;
        const r = c.getBoundingClientRect(), step = 14;
        let dx = 0, dy = 0;
        if (cur.y < r.top + EDGE_PX && cur.y > r.top - 40) dy = step;
        else if (cur.y > r.bottom - EDGE_PX && cur.y < r.bottom + 40) dy = -step;
        if (cur.x < r.left + EDGE_PX && cur.x > r.left - 40) dx = step;
        else if (cur.x > r.right - EDGE_PX && cur.x < r.right + 40) dx = -step;
        if (dx || dy) setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
      }, 16);
    }
    e.preventDefault();
    const t = targetAt(e.clientX, e.clientY);
    s.target = t;
    hoverToOpen(t);
    setDrag({ id: s.id, x: e.clientX, y: e.clientY, target: t, ok: canDropOn(s.id, t) });
  };
  handlers.current.end = (commit) => {
    const s = session.current;
    session.current = null;
    window.removeEventListener("pointermove", onWinMove);
    window.removeEventListener("pointerup", onWinUp);
    window.removeEventListener("pointercancel", onWinCancel);
    window.removeEventListener("keydown", onWinKey);
    clearInterval(scrollTimer.current);
    stopHoverOpen();
    document.body.classList.remove("oc-drag-active");
    setDrag(null);
    if (!s || !s.started) return;
    suppressClick.current = true;
    setTimeout(() => { suppressClick.current = false; }, 0);
    if (commit && canDropOn(s.id, s.target)) moveNow(s.id, s.target === "top" ? null : s.target);
  };
  // Stable listener functions (same reference for add / remove)
  const onWinMove = useRef((e) => handlers.current.move(e)).current;
  const onWinUp = useRef(() => handlers.current.end(true)).current;
  const onWinCancel = useRef(() => handlers.current.end(false)).current;
  const onWinKey = useRef((e) => { if (e.key === "Escape") handlers.current.end(false); }).current;
  useEffect(() => () => handlers.current.end(false), []);   // leaving the page mid-drag

  const pressProps = (p) =>
    canEdit && p.active
      ? {
          onPointerDown: (e) => {
            // mouse / pen / trackpad; touch screens use "Move…" (a finger drag scrolls the chart)
            if (e.button !== 0 || e.pointerType === "touch" || movingIds.has(p.id) || moveFor != null) return;
            session.current = { id: p.id, sx: e.clientX, sy: e.clientY, x: e.clientX, y: e.clientY, started: false, target: null };
            window.addEventListener("pointermove", onWinMove);
            window.addEventListener("pointerup", onWinUp);
            window.addEventListener("pointercancel", onWinCancel);
            window.addEventListener("keydown", onWinKey);
          },
        }
      : {};

  // ── "Move…" without dragging: pick the person, then click the new manager ──
  useEffect(() => {
    if (moveFor == null) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMoveFor(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [moveFor]);
  const startMove = (id) => { setSelectedId(null); setMoveFor(id); };
  const finishMove = (targetId) => {
    const src = moveFor;
    if (!canDropOn(src, targetId)) { messageApi.warning(whyNot(src, targetId)); return; }
    setMoveFor(null);
    moveNow(src, targetId === "top" ? null : targetId);
  };

  const onCardClick = (p) => {
    if (suppressClick.current) return;
    if (moveFor != null) { finishMove(p.id); return; }
    setSelectedId(p.id);
  };

  const paletteList = useMemo(() => {
    const q = paletteFilter.trim().toLowerCase();
    return active
      .filter((p) => !q || `${p.name} ${p.designation} ${p.code}`.toLowerCase().includes(q))
      .sort((a, b) => (managerOf(a) == null ? 0 : 1) - (managerOf(b) == null ? 0 : 1) || a.name.localeCompare(b.name));
  }, [active, paletteFilter, managerOf]);

  // ── Add / edit ──
  const openEditor = (person) => {
    setEditing(person);
    form.resetFields();
    form.setFieldsValue({
      code: person.code || "",
      name: person.name || "",
      email: person.email || "",
      phone: person.phone || "",
      region: person.region || "",
      designation: person.designation || "",
      managerId: person.managerId ?? undefined,
      joined: person.joined ? dayjs(person.joined) : null,
      status: person.status || "Active",
      relieving: person.relievingDate ? dayjs(person.relievingDate) : null,
    });
  };
  const submitEditor = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const res = await saveOrgPerson({
        id: editing.id || 0,
        code: v.code.trim(),
        name: v.name.trim(),
        email: (v.email || "").trim(),
        phone: (v.phone || "").trim(),
        region: (v.region || "").trim(),
        designation: (v.designation || "").trim(),
        managerId: v.managerId || null,
        joined: v.joined ? v.joined.format("YYYY-MM-DD") : null,
        status: v.status || "Active",
        relievingDate: v.status !== "Active" && v.relieving ? v.relieving.format("YYYY-MM-DD") : null,
      });
      messageApi.success(res.message);
      setEditing(null);
      await load();
      if (res.person) reveal(res.person.id);
    } catch (err) {
      messageApi.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  const managerOptions = useMemo(() => {
    const blocked = editing?.id ? new Set([editing.id, ...teamOf(editing.id)]) : new Set();
    return active
      .filter((p) => !blocked.has(p.id))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => ({ value: p.id, label: `${p.name}${p.designation ? ` · ${p.designation}` : ""} · ${p.code}` }));
  }, [active, editing, teamOf]);

  // ── Relieved / reactivate ──
  // "Mark relieved" = relieved today. For another date (or Notice Period), use Edit → Status.
  const confirmInactive = (p) => {
    const reports = kids(p.id).length;
    const up = managerOf(p) ? byId.get(managerOf(p)).name : "the top level";
    AntdModal.confirm({
      title: `Mark ${p.name} as relieved today?`,
      content: `${p.name} will be removed from the chart (relieving date ${dayjs().format("DD MMM YYYY")}).${reports ? ` Their ${reports} direct report${reports === 1 ? "" : "s"} will move to ${up}.` : ""} For a different date or Notice Period, use Edit → Status. You can reactivate them later.`,
      okText: "Mark relieved",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const msg = await setOrgPersonActive(p.id, false, dayjs().format("YYYY-MM-DD"));
          messageApi.success(msg);
          setSelectedId(null);
          await load();
        } catch (err) {
          messageApi.error(err.message);
        }
      },
    });
  };
  const reactivate = async (p) => {
    try {
      const msg = await setOrgPersonActive(p.id, true, dayjs().format("YYYY-MM-DD"));
      messageApi.success(msg);
      await load();
      reveal(p.id);
    } catch (err) {
      messageApi.error(err.message);
    }
  };

  // ── Rendering ──
  const dragOkTarget = drag && drag.ok ? drag.target : null;   // highlighted drop target
  const renderCard = (p, depth, opts = {}) => {
    const lv = LEVELS[depth % LEVELS.length];
    const cls = ["oc-card"];
    if (movingIds.has(p.id)) cls.push("oc-changed");
    if (selectedId === p.id) cls.push("oc-selected");
    if (foundId === p.id) cls.push("oc-found");
    if (dragOkTarget === p.id) cls.push("oc-drop-ok");
    if (drag && drag.id === p.id) cls.push("oc-dragging");
    if (moveFor === p.id) cls.push("oc-moving");
    else if (moveFor != null && p.active) cls.push(canDropOn(moveFor, p.id) ? "oc-move-target" : "oc-move-no");
    if (canEdit && p.active && moveFor == null) cls.push("oc-grab");
    return (
      <div
        key={p.id}
        id={`${opts.idPrefix || "oc-node-"}${p.id}`}
        className={cls.join(" ")}
        style={{ borderLeftColor: p.active ? lv.bar : "#9AA3B2", background: p.active ? lv.bg : "#F4F5F7" }}
        role="button"
        tabIndex={0}
        data-oc-drop={p.active ? p.id : undefined}
        title={`${p.name}${p.designation ? `\n${p.designation}` : ""}\nEmp ID - ${p.code}${canEdit && p.active ? "\nDrag onto a manager to move" : ""}`}
        onClick={() => onCardClick(p)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onCardClick(p); } }}
        {...pressProps(p)}
      >
        <Avatar name={p.name} inactive={!p.active} />
        <div className="oc-text">
          <div className="oc-name">{p.name}</div>
          <div className="oc-role">{p.designation || "—"}</div>
          <div className="oc-code">Emp ID - {p.code}</div>
          {opts.note && <div className={`oc-note ${opts.noteWarn ? "oc-note-warn" : ""}`}>{opts.note}</div>}
          {/* Notice Period: still on the chart, leaving soon */}
          {!opts.note && p.active && p.status === "Notice Period" && (
            <div className="oc-leaving">Leaving {fmtDate(p.relievingDate)}</div>
          )}
        </div>
      </div>
    );
  };

  // The whole <li> (card + open team) is a drop area for that manager
  const renderNode = (p, depth) => {
    const list = kids(p.id);
    const open = list.length > 0 && !collapsed.has(p.id);
    return (
      <li key={p.id} data-oc-drop={p.id} className={dragOkTarget === p.id ? "oc-li-drop" : undefined}>
        {renderCard(p, depth)}
        {list.length > 0 && (
          <button type="button" className="oc-toggle" onClick={() => toggle(p.id)}
            aria-expanded={open} aria-label={open ? `Hide ${p.name}'s team` : `Show ${p.name}'s team (${list.length})`}>
            {open ? "−" : list.length}
          </button>
        )}
        {open && renderTeam(list, depth + 1)}
      </li>
    );
  };

  // A team: people who have their own team side by side; people without a team stacked in one column
  const renderTeam = (list, depth) => {
    const branches = list.filter((c) => kids(c.id).length > 0);
    const leaves = list.filter((c) => kids(c.id).length === 0);
    return (
      <ul>
        {branches.map((c) => renderNode(c, depth))}
        {leaves.length > 0 && (
          <li key="stack">
            <div className="oc-stack">
              {leaves.map((c) => (
                <div key={c.id} className="oc-stack-item">{renderCard(c, depth)}</div>
              ))}
            </div>
          </li>
        )}
      </ul>
    );
  };

  const selected = selectedId != null ? byId.get(selectedId) : null;
  const selectedManager = selected && selected.managerId ? byId.get(selected.managerId) : null;
  const selectedTeam = selected ? kids(selected.id) : [];
  const dragged = drag ? byId.get(drag.id) : null;
  const moving = moveFor != null ? byId.get(moveFor) : null;

  if (state === "loading") {
    return <div className="oc-page p-8 flex items-center gap-3"><Spin /> <span className="text-[14px] text-[#5B6474]">Loading org chart…</span></div>;
  }
  if (state === "error") {
    return (
      <div className="oc-page p-8">
        <h2 className="page-heading">ORG CHART</h2>
        <p className="text-[14px] text-[#5B6474]">{error}</p>
        <Button onClick={() => { setState("loading"); load(); }}>Try again</Button>
      </div>
    );
  }

  return (
    <div className="oc-page">
      {contextHolder}
      <div className="mb-[5px]">
        <h2 className="page-heading relative bottom-[-10px]">ORG CHART</h2>
      </div>

      <div className="oc-toolbar mt-6">
        <Select
          showSearch
          allowClear
          value={null}
          placeholder="Search by name, role or Emp ID"
          options={searchOptions}
          optionFilterProp="label"
          onSelect={(id) => reveal(id)}
          style={{ width: 320, maxWidth: "100%" }}
          size="large"
          notFoundContent="No one found"
        />
        <div className="oc-tools">
          <span className="text-[12px] text-[#5B6474]">{active.length} people</span>
          <Button size="small" onClick={expandAll}>Expand all</Button>
          <Button size="small" onClick={collapseAll}>Collapse all</Button>
          <span className="oc-zoom" role="group" aria-label="Zoom">
            <button type="button" onClick={() => zoomBy(-0.1)} aria-label="Zoom out">−</button>
            <span>{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => zoomBy(0.1)} aria-label="Zoom in">+</button>
            <button type="button" onClick={fit} style={{ fontSize: 12, padding: "0 8px" }}>Fit</button>
            <button type="button" onClick={zoomTo100} style={{ fontSize: 12, padding: "0 8px" }}>100%</button>
          </span>
          {canEdit && (
            <>
              <span className="flex items-center gap-1.5 text-[12px] text-[#3D4555]">
                <Switch size="small" checked={showInactive} onChange={setShowInactive} /> Relieved ({inactive.length})
              </span>
              <Button onClick={() => { setShowPalette((v) => !v); setPaletteFilter(""); }}>
                {showPalette ? "Hide employee list" : `All employees (${active.length})`}
              </Button>
              <Button type="primary" onClick={() => openEditor({ id: 0 })}>Add employee</Button>
            </>
          )}
        </div>
      </div>

      {canEdit && moveFor == null && (
        <p className="m-0 mt-2 text-[12px] text-[#5B6474]">
          To change who someone reports to: <b>drag their card onto the new manager</b> (their card or anywhere in their team turns
          green). Or click the card → <b>Move…</b> → click the new manager. Saved at once, with Undo. Approvals follow the new line straight away.
          {" "}<b>Mouse wheel</b> zooms in / out; <b>drag empty space</b> to move the whole chart.
        </p>
      )}

      {/* "Move…" mode: click the new manager */}
      {moving && (
        <div className="oc-movebar" role="status">
          <span>Moving <b>{moving.name}</b>: click the card of the person they will report to.</span>
          <span className="oc-movebar-actions">
            {canDropOn(moving.id, "top") && <Button size="small" onClick={() => finishMove("top")}>No manager (top level)</Button>}
            <Button size="small" onClick={() => setMoveFor(null)}>Cancel (Esc)</Button>
          </span>
        </div>
      )}

      {/* Everyone, ready to drag (Admin / HR) */}
      {canEdit && showPalette && (
        <div className="oc-tray oc-small oc-palette">
          <div className="oc-tray-title">
            <strong>All employees ({active.length})</strong>
            <span>{unplaced.length} without a manager, listed first. Drag a card onto their manager in the chart (or onto another card here).</span>
            <Input size="small" allowClear placeholder="Filter by name, role or Emp ID" value={paletteFilter}
              onChange={(e) => setPaletteFilter(e.target.value)} style={{ width: 240 }} />
          </div>
          <div className="oc-palette-grid">
            {paletteList.length === 0
              ? <span className="text-[12px] text-[#5B6474]">No one matches.</span>
              : paletteList.map((p) => {
                  const m = managerOf(p);
                  return renderCard(p, 0, {
                    idPrefix: "oc-pal-",
                    note: m ? `Reports to ${byId.get(m).name}` : pinnedTop.has(p.id) || kids(p.id).length ? "Top level" : "No manager yet",
                    noteWarn: !m && !pinnedTop.has(p.id) && !kids(p.id).length,
                  });
                })}
          </div>
        </div>
      )}

      {/* View: people with no manager and no team yet */}
      {!showPalette && unplaced.length > 0 && (
        <div className="oc-tray oc-small">
          <div className="oc-tray-title">
            <strong>No manager yet ({unplaced.length})</strong>
            {canEdit && <span>New people land here. Drag them onto their manager in the chart.</span>}
          </div>
          <div className="oc-tray-list">
            {unplaced.map((p) => renderCard(p, 0))}
          </div>
        </div>
      )}

      {canEdit && showInactive && (
        <div className="oc-tray oc-small">
          <div className="oc-tray-title"><strong>Relieved ({inactive.length})</strong><span>Click a card to see details or reactivate.</span></div>
          <div className="oc-tray-list">
            {inactive.length === 0
              ? <span className="text-[12px] text-[#5B6474]">No relieved people.</span>
              : [...inactive].sort((a, b) => a.name.localeCompare(b.name))
                  .map((p) => renderCard(p, 0, { note: `Relieved ${fmtDate(p.relievingDate || p.inactiveDate)}`, noteWarn: true }))}
          </div>
        </div>
      )}

      <div className={`oc-canvas ${panning ? "oc-panning" : ""}`} ref={canvasRef} onPointerDown={onCanvasPointerDown}>
        {/* Only on an empty chart: a box to place the very first (top) person */}
        {canEdit && roots.length === 0 && (
          <div className={`oc-topzone ${dragOkTarget === "top" ? "oc-drop-ok" : ""}`} data-oc-drop="top"
            onClick={() => moveFor != null && finishMove("top")}>
            Drop the top person here (e.g. Managing Director), then drag their team onto them.
          </div>
        )}
        {roots.length === 0 ? (
          <div className="p-8 text-[14px] text-[#5B6474]">
            {active.length === 0
              ? "No one on the org chart yet. Run OrgChart_Setup.sql, or add people with “Add employee”."
              : canEdit
                ? "Start with the top person: drag them onto “Top level” above, then drag their team onto them."
                : "No reporting lines yet."}
          </div>
        ) : (
          <div
            className={`oc-stage ${smooth ? "oc-smooth" : ""}`}
            ref={stageRef}
            style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${zoom})`, visibility: centered.current ? "visible" : "hidden" }}
          >
            <ul className="oc-tree">
              {roots.map((r) => renderNode(r, 0))}
            </ul>
          </div>
        )}
      </div>

      {/* While dragging: "no manager" bar at the bottom of the screen + a label next to the pointer */}
      {drag && dragged && roots.length > 0 && canDropOn(drag.id, "top") && (
        <div className={`oc-topzone oc-topzone-float ${dragOkTarget === "top" ? "oc-drop-ok" : ""}`} data-oc-drop="top">
          Drop here: <b>no manager (top level)</b>
        </div>
      )}
      {drag && dragged && (
        <div className={`oc-ghost ${drag.ok ? "ok" : "no"}`} style={{ left: drag.x + 14, top: drag.y + 14 }} aria-hidden="true">
          <Avatar name={dragged.name} />
          <span>
            <b>{dragged.name}</b>
            <small>
              {drag.ok
                ? (drag.target === "top" ? "→ No manager (top level)" : `→ Reports to ${byId.get(drag.target)?.name}`)
                : whyNot(drag.id, drag.target)}
            </small>
          </span>
        </div>
      )}

      {/* Details */}
      <Drawer open={!!selected} onClose={() => setSelectedId(null)} width={360} title={null} className="oc-drawer" destroyOnClose>
        {selected && (
          <div className="oc-page">
            <div className="oc-panel-head">
              <Avatar name={selected.name} inactive={!selected.active} />
              <div style={{ minWidth: 0 }}>
                <div className="text-[17px] font-semibold">{selected.name}</div>
                <div className="text-[13px] text-[#3D4555] font-medium">{selected.designation || "—"}</div>
                <div className="text-[12px] text-[#5B6474]">Emp ID - {selected.code}</div>
                {!selected.active && (
                  <div className="text-[12px] text-[#C2410C] font-semibold mt-1">Relieved on {fmtDate(selected.relievingDate || selected.inactiveDate)}</div>
                )}
                {selected.active && selected.status === "Notice Period" && (
                  <div className="text-[12px] text-[#C2410C] font-semibold mt-1">Notice Period · leaving {fmtDate(selected.relievingDate)}</div>
                )}
              </div>
            </div>

            <div className="oc-sec">
              <h4>Contact Details</h4>
              <Field label="Mobile">{selected.phone && <a href={`tel:${selected.phone.replace(/[^0-9+]/g, "")}`}>{selected.phone}</a>}</Field>
              <Field label="Email Id">{selected.email && <a href={`mailto:${selected.email}`}>{selected.email}</a>}</Field>
            </div>

            <div className="oc-sec">
              <h4>Category</h4>
              <Field label="Region">{selected.region}</Field>
            </div>

            <div className="oc-sec">
              <h4>Other Information</h4>
              <Field label="Joining Date">{selected.joined && fmtDate(selected.joined)}</Field>
              <Field label="Status">{selected.status}</Field>
              <Field label="Relieving Date">{selected.relievingDate && fmtDate(selected.relievingDate)}</Field>
              {canEdit && selected.active ? (
                <div className="oc-field">
                  <label>Reporting To (pick to change, saves at once)</label>
                  <Select
                    showSearch
                    style={{ width: "100%", marginTop: 4 }}
                    value={managerOf(selected) ?? "__top"}
                    optionFilterProp="label"
                    disabled={movingIds.has(selected.id)}
                    loading={movingIds.has(selected.id)}
                    options={[
                      { value: "__top", label: "No manager (top level)" },
                      ...active
                        .filter((p) => p.id !== selected.id && !teamOf(selected.id).has(p.id))   // not themselves or their own team
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((p) => ({ value: p.id, label: `${p.name}${p.designation ? ` · ${p.designation}` : ""} · ${p.code}` })),
                    ]}
                    onChange={(v) => moveNow(selected.id, v === "__top" ? null : v)}
                  />
                  {selectedManager && (
                    <span className="oc-link text-[12px]" onClick={() => reveal(selectedManager.id)}>Show {selectedManager.name} on the chart</span>
                  )}
                </div>
              ) : (
                <Field label="Reporting To">
                  {selectedManager && (
                    <span className="oc-link" onClick={() => reveal(selectedManager.id)}>
                      {selectedManager.name} [{selectedManager.code}]
                    </span>
                  )}
                </Field>
              )}
              <Field label={`Direct Reports${selectedTeam.length ? ` (${selectedTeam.length})` : ""}`}>
                {selectedTeam.length > 0 && selectedTeam.map((c, i) => (
                  <span key={c.id}>
                    {i > 0 && ", "}
                    <span className="oc-link" onClick={() => reveal(c.id)}>{c.name}</span>
                  </span>
                ))}
              </Field>
            </div>

            {canEdit && (
              <div className="oc-sec" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button type="primary" onClick={() => openEditor(selected)}>Edit</Button>
                {selected.active ? (
                  <>
                    <Tooltip title="Then click the card of the person they will report to">
                      <Button onClick={() => startMove(selected.id)}>Move…</Button>
                    </Tooltip>
                    <Tooltip title={`Add someone who reports to ${selected.name}`}>
                      <Button onClick={() => openEditor({ id: 0, managerId: selected.id })}>Add team member</Button>
                    </Tooltip>
                    <Button danger onClick={() => confirmInactive(selected)}>Mark relieved</Button>
                  </>
                ) : (
                  <Button onClick={() => reactivate(selected)}>Reactivate</Button>
                )}
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* Add / edit */}
      <AntdModal
        open={!!editing}
        title={editing?.id ? `Edit ${editing.name}` : "Add employee"}
        onCancel={() => setEditing(null)}
        onOk={submitEditor}
        okText={editing?.id ? "Save" : "Add"}
        confirmLoading={saving}
        maskClosable={false}
        forceRender      /* keep the form mounted so Edit can fill it before the dialog opens */
        zIndex={1100}    /* above the details panel */
        width={620}
      >
        <Form form={form} layout="vertical" requiredMark>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item label="Emp ID" name="code"
              rules={[{ required: true, message: "Enter the Emp ID" },
                      { pattern: /^[A-Za-z0-9_-]{1,20}$/, message: "Letters, numbers, - or _ (max 20)" }]}>
              <Input placeholder="e.g. RCK1240" />
            </Form.Item>
            <Form.Item label="Name" name="name" rules={[{ required: true, whitespace: true, message: "Enter the name" }, { max: 200 }]}>
              <Input placeholder="Full name" />
            </Form.Item>
            <Form.Item label="Email" name="email" rules={[{ type: "email", message: "Enter a valid email" }, { max: 256 }]}>
              <Input placeholder="name@rcktechiees.com" />
            </Form.Item>
            <Form.Item label="Phone" name="phone" rules={[{ pattern: /^[0-9+()\-\s]{6,30}$/, message: "Digits, spaces, +, - and ( ) only" }]}>
              <Input placeholder="+91 …" />
            </Form.Item>
            <Form.Item label="Role" name="designation" rules={[{ max: 200 }]}>
              <Input placeholder="e.g. Project Manager" />
            </Form.Item>
            <Form.Item label="Region" name="region" rules={[{ max: 100 }]}>
              <AutoComplete options={regions.map((r) => ({ value: r }))} placeholder="e.g. Chennai"
                filterOption={(input, o) => o.value.toLowerCase().includes(input.toLowerCase())} />
            </Form.Item>
            <Form.Item label="Reporting to" name="managerId">
              <Select showSearch allowClear placeholder="No manager (top level)" options={managerOptions} optionFilterProp="label" />
            </Form.Item>
            <Form.Item label="Joining date" name="joined">
              <DatePicker className="w-full" format="DD-MM-YYYY" />
            </Form.Item>
            {/* Status + relieving date */}
            <Form.Item label="Status" name="status">
              <Select
                options={STATUSES.map((s) => ({
                  value: s, label: s, disabled: s === "Relieved" && !editing?.id,   // a new person can't start as Relieved
                }))}
                onChange={(s) => {
                  if (s === "Active") form.setFieldsValue({ relieving: null });
                  else if (s === "Relieved" && !form.getFieldValue("relieving")) form.setFieldsValue({ relieving: dayjs() });
                }}
              />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(a, b) => a.status !== b.status}>
              {({ getFieldValue }) => {
                const st = getFieldValue("status") || "Active";
                return (
                  <Form.Item
                    label="Relieving date"
                    name="relieving"
                    rules={[{ required: st !== "Active", message: "Enter the relieving date" }]}
                    extra={st === "Relieved" ? "Removed from the chart. Their team moves to their manager."
                         : st === "Notice Period" ? "Stays on the chart until this date, then is relieved automatically."
                         : null}
                  >
                    <DatePicker className="w-full" format="DD-MM-YYYY" disabled={st === "Active"} placeholder={st === "Active" ? "—" : "Select date"} />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>
        </Form>
      </AntdModal>
    </div>
  );
};

export default OrgChart;